import React from 'react';
import ArrayBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ArrayBuilder';
import { ChatAttachment, AttachmentMetadataType } from '@scale/shared/types/chat';
import { Button, Typography } from '@scale/scaleui';
import { BuilderInputTypes, ObjectSchema } from '@scale/shared/taskTemplate/types';
import type { TypeBuilderMapType } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemasType';

const MediaAttachmentsSchema: ObjectSchema = {
  attachmentType: {
    displayName: 'Attachment Type',
    required: true,
    inputType: BuilderInputTypes.AttachmentsChoices,
  },
  metadata: {
    displayName: 'Metadata',
    required: true,
    inputType: BuilderInputTypes.String,
    hint: 'The content of the attachment',
  },
  caption: {
    displayName: 'Caption',
    required: false,
    inputType: BuilderInputTypes.String,
    hint: 'The caption of the emails',
  },
};

const ImageAttachmentsSchema: ObjectSchema = {
  attachmentType: {
    displayName: 'Attachment Type',
    required: true,
    inputType: BuilderInputTypes.AttachmentsChoices,
  },
  url: {
    displayName: 'URL',
    required: true,
    inputType: BuilderInputTypes.String,
    hint: 'The URL of the image',
  },
  caption: {
    displayName: 'Caption',
    required: false,
    inputType: BuilderInputTypes.String,
    hint: 'The caption of the image',
  },
};

const IFrameAttachmentsSchema: ObjectSchema = {
  attachmentType: {
    displayName: 'Attachment Type',
    required: true,
    inputType: BuilderInputTypes.AttachmentsChoices,
  },
  url: {
    displayName: 'URL',
    required: true,
    inputType: BuilderInputTypes.String,
    hint: 'The URL of the iframe',
  },
  caption: {
    displayName: 'Caption',
    required: false,
    inputType: BuilderInputTypes.String,
    hint: 'The caption of the iframe',
  },
};

const AudioAttachmentSchema: ObjectSchema = {
  attachmentType: {
    displayName: 'Attachment Type',
    required: true,
    inputType: BuilderInputTypes.AttachmentsChoices,
  },
  url: {
    displayName: 'URL',
    required: true,
    inputType: BuilderInputTypes.String,
    hint: 'The URL of the audio',
  },
  caption: {
    displayName: 'Caption',
    required: false,
    inputType: BuilderInputTypes.String,
    hint: 'The caption of the audio',
  },
};

const AttachmentsTypeSchemaMap: Record<AttachmentMetadataType, ObjectSchema> = {
  [AttachmentMetadataType.Email]: MediaAttachmentsSchema,
  [AttachmentMetadataType.BulbaIceFlowState]: MediaAttachmentsSchema,
  [AttachmentMetadataType.BulbaIceRequest]: MediaAttachmentsSchema,
  [AttachmentMetadataType.BulbaIceResponseText]: MediaAttachmentsSchema,
  [AttachmentMetadataType.FilePreprocessContext]: MediaAttachmentsSchema,
  [AttachmentMetadataType.CodeExecutionMetadata]: MediaAttachmentsSchema,
  [AttachmentMetadataType.BulbaToolPromptGenMetadata]: MediaAttachmentsSchema,
  [AttachmentMetadataType.BulbaToolCodeExecutionMetadata]: MediaAttachmentsSchema,
  [AttachmentMetadataType.BulbaIceGenerateContentResponse]: MediaAttachmentsSchema,
  [AttachmentMetadataType.Image]: ImageAttachmentsSchema,
  [AttachmentMetadataType.IFrame]: IFrameAttachmentsSchema,
  [AttachmentMetadataType.Audio]: AudioAttachmentSchema,
};

interface MediaAttachmentsBuilderProps {
  name: string;
  existingValue?: Array<ChatAttachment>;
  updateProperty: (arg: Array<ChatAttachment>) => void;
  required?: boolean;
  hint?: string;
  typeBuilderMap: TypeBuilderMapType;
}

const MediaAttachmentsBuilder: React.FC<MediaAttachmentsBuilderProps> = ({
  name,
  existingValue = [],
  updateProperty,
  typeBuilderMap,
}) => {
  const providedAttachments = existingValue;

  const addProvidedAttachmentsResponses = () => {
    const newProvidedAttachmentsResponses = [...providedAttachments];
    newProvidedAttachmentsResponses.push({
      attachmentType: AttachmentMetadataType.Email,
      metadata: '',
    });
    updateProperty(newProvidedAttachmentsResponses);
  };

  return (
    <div className="flex flex-col gap-1">
      {name && <Typography.Subtitle3>{name}</Typography.Subtitle3>}
      <ArrayBuilder
        typeBuilderMap={typeBuilderMap}
        elements={providedAttachments}
        setElements={updateProperty}
        getKey={attachmentResponse => attachmentResponse.attachmentType}
        getTitle={attachmentResponse => attachmentResponse.attachmentType}
        getSchema={attachmentResponse =>
          AttachmentsTypeSchemaMap[attachmentResponse.attachmentType]
        }
      />
      <div>
        <Button size="sm" onClick={() => addProvidedAttachmentsResponses()} variant="outline">
          Add Attachment
        </Button>
      </div>
    </div>
  );
};

export default MediaAttachmentsBuilder;
